import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRouteSnapshot, CanActivateFn, UrlSegment } from '@angular/router';
import { UserService, UserType } from '../services/user-management/user.service';
import { SessionService } from '../services/user-management/session.service';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from '../services/user-management/auth.service';
import { SystemService } from '../services/system.service';
import { ProfessionalScreenerService } from '../services/self-takers/professional-screener.service';
import { InboxService } from '../services/inbox/inbox.service';
import { ScreenerCenterService } from '../services/self-takers/screener-center.service';
import { HealthSecretaryService } from '../services/self-takers/health-secretary.service';
import { ClinicalLaboratoryService } from '../services/self-takers/clinical-laboratory.service';
import { SocketIoService } from '../services/web-socket/socket-io.service';
import { NurseService } from '../services/treatments/nurse.service';
import { DispenserService } from '../services/treatments/dispenser.service';
import { InsurerService } from '../services/treatments/insurer.service';
import { IpsService } from '../services/treatments/ips.service';
import { PharmaceuticalService } from '../services/treatments/pharmaceutical.service';
import { DoctorService } from '../services/treatments/doctor.service';
import { PointsService } from '../services/points/points.service';

export const authGuard: CanActivateFn = (route, state) => {

  const userService = inject(UserService);
  const sessionService = inject(SessionService);
  const authService = inject(AuthService);
  const platformId = inject(PLATFORM_ID);
  const inboxService = inject(InboxService);
  const socketService = inject(SocketIoService);
  const pointsService = inject(PointsService);
  const systemService = inject(SystemService);

  /* Self-takers services */
  const professionalScreenerService = inject(ProfessionalScreenerService);
  const screenerCenterService = inject(ScreenerCenterService);
  const healthSecretaryService = inject(HealthSecretaryService);
  const clinicalLaboratoryService = inject(ClinicalLaboratoryService);

  /* Treatments services */
  const nurseService = inject(NurseService);
  const dispenserService = inject(DispenserService);
  const insurerService = inject(InsurerService);
  const ipsService = inject(IpsService);
  const pharmaceuticalService = inject(PharmaceuticalService);
  const doctorService = inject(DoctorService);

  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  const token = sessionService.token();

  if(token) {
    try {
      const decodedToken = jwtDecode(token);

      if (!decodedToken.sub || decodedToken.sub === 'public') {
        authService.logout();
        return false;
      }

      const userType = userService.userData()?.user_type;
      const finalRoute = getFullRoute(route);

      if (!allowedPagesForProfessionalScreener(finalRoute) && userType == UserType.PROFESSIONAL_SCREENER) {
        return false;
      }

      if (!allowedPagesForScreenerCenter(finalRoute) && userType == UserType.SCREENER_CENTER) {
        return false;
      }

      if (!allowedPagesForHealthSecretary(finalRoute) && userType == UserType.HEALTH_SECRETARY) {
        return false;
      }

      if (!allowedPagesForClinicalLaboratory(finalRoute) && userType == UserType.CLINICAL_LABORATORY) {
        return false;
      }

      if (!allowedPagesForInsurer(finalRoute) && userType == UserType.INSURER) {
        return false;
      }

      if (!allowedPagesForDispenser(finalRoute) && userType == UserType.DISPENSER) {
        return false;
      }

      if (!allowedPagesForIps(finalRoute) && userType == UserType.IPS) {
        return false;
      }

      if (!allowedPagesForPharmaceutical(finalRoute) && userType == UserType.PHARMACEUTICAL) {
        return false;
      }

      if (!allowedPagesForNurse(finalRoute) && userType == UserType.NURSE) {
        return false;
      }

      if (!allowedPagesForDoctor(finalRoute) && userType == UserType.DOCTOR) {
        return false;
      }

      if (userType == UserType.PROFESSIONAL_SCREENER) {
        console.log("professional screener user");
        systemService.getCityList();
        systemService.getEpsList();
        professionalScreenerService.getMyData();
        inboxService.getConversations();
        // pointsService.getMyPointsOverview();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      if (userType == UserType.SCREENER_CENTER) {
        console.log("screener center user");
        screenerCenterService.getMyData();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      if (userType == UserType.HEALTH_SECRETARY) {
        console.log("Health secretary user");
        healthSecretaryService.getMyData();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      if (userType == UserType.CLINICAL_LABORATORY) {
        console.log("Clinical laboratory user");
        clinicalLaboratoryService.getMyData();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }


      if (userType == UserType.DOCTOR) {
        console.log("Doctor user");
        inboxService.getConversations();
        systemService.getEpsList();
        systemService.getCityList();
        doctorService.getMyData();
        pointsService.getMyPointsOverview();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      if (userType == UserType.NURSE) {
        console.log("Nurse user");
        inboxService.getConversations();
        systemService.getEpsList();
        systemService.getCityList();
        nurseService.getMyData();
        pointsService.getMyPointsOverview();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      if (userType == UserType.PHARMACEUTICAL) {
        console.log("Pharmaceutical user");
        systemService.getEpsList();
        systemService.getCityList();
        pharmaceuticalService.getMyData();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      if (userType == UserType.IPS) {
        console.log("IPS user");
        systemService.getEpsList();
        systemService.getCityList();
        ipsService.getMyData();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      if (userType == UserType.DISPENSER) {
        console.log("Dispenser user");
        dispenserService.getMyData();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      if (userType == UserType.INSURER) {
        console.log("Insurer user");
        insurerService.getMyData();
        socketService.emit('login', {user_id: userService.userData()?.id, user_type: userService.userData()?.user_type});
      }

      return true;
    }
    catch (error) {
      authService.logout();
      return false;
    }
  }


  authService.logout();
  return false;
};


function getFullRoute(route: ActivatedRouteSnapshot): string {
  const segments: UrlSegment[] = route.url;
  return `/${segments.map(segment => segment.path).join('/')}`;
}

/* Self takers allowed pages */

function allowedPagesForProfessionalScreener(route: string): boolean {
  const pagesForProfessionalScreener: string[] = [
    '/professional-screener',
    '/settings'
  ];
  return pagesForProfessionalScreener.includes(route);
}

function allowedPagesForScreenerCenter(route: string): boolean {
  const pagesForScreenerCenter: string[] = [
    '/screener-center',
    '/settings'
  ];
  return pagesForScreenerCenter.includes(route);
}

function allowedPagesForHealthSecretary(route: string): boolean {
  const pagesForHealthSecretary: string[] = [
    '/health-secretary',
    '/settings'
  ];
  return pagesForHealthSecretary.includes(route);
}

function allowedPagesForClinicalLaboratory(route: string): boolean {
  const pagesForClinicalLaboratory: string[] = [
    '/clinical-laboratory',
    '/settings'
  ];
  return pagesForClinicalLaboratory.includes(route);
}

/* Treatments allowed pages */

function allowedPagesForInsurer(route: string): boolean {
  const pagesForInsurer: string[] = [
    '/insurer',
    '/settings'
  ];
  return pagesForInsurer.includes(route);
}

function allowedPagesForDispenser(route: string): boolean {
  const pagesForDispenser: string[] = [
    '/dispenser',
    '/settings'
  ];
  return pagesForDispenser.includes(route);
}

function allowedPagesForIps(route: string): boolean {
  const pagesForIps: string[] = [
    '/ips',
    '/settings'
  ];
  return pagesForIps.includes(route);
}

function allowedPagesForPharmaceutical(route: string): boolean {
  const pagesForPharmaceutical: string[] = [
    '/pharmaceutical',
    '/settings'
  ];
  return pagesForPharmaceutical.includes(route);
}

function allowedPagesForNurse(route: string): boolean {
  const pagesForNurse: string[] = [
    '/nurse',
    '/settings'
  ];
  return pagesForNurse.includes(route);
}

function allowedPagesForDoctor(route: string): boolean {
  const pagesForDoctor: string[] = [
    '/doctor',
    '/settings'
  ];
  return pagesForDoctor.includes(route);
}